import {
  ARCHIVED_CLIENT_LOCKED_SECTIONS,
  isClientArchived,
} from "../../../Tools/ArchivedClient";
import {
  Badge,
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { FC } from "react";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { IRouteParams } from "src/Modules/Company/Interfaces";
import { useStyles } from "./CompanySidebar.styles";
import {
  AccessTimeOutlined,
  AddBoxOutlined,
  AssignmentOutlined,
  ChatBubbleOutline,
  ContentCopyOutlined,
  InsertChartOutlined,
  PeopleOutline,
  PersonOutline,
  TimelineOutlined,
  WorkOutline,
  TrendingUp,
  HelpOutline,
} from "@mui/icons-material";
import { useStore } from "src/Shared/Hooks";
import { MenuStatus } from "src/Modules/Company/Enums";

export const iconMap: Record<string, React.ElementType> = {
  AssignmentOutlined,
  PersonOutline,
  AddBoxOutlined,
  PeopleOutline,
  WorkOutline,
  ChatBubbleOutline,
  ContentCopyOutlined,
  AccessTimeOutlined,
  TrendingUp,
  TimelineOutlined,
};
const CompanySidebar: FC = () => {
  const { companyRowID } = useParams<Partial<IRouteParams>>();

  const location = useLocation();

  const classes = useStyles();

  const {
    rootStore: {
      companyStore: { menu, companyOverview },
    },
  } = useStore();

  /* ========================= */
  /* Detect Mode */
  /* ========================= */

  const isAddMode = location.pathname.startsWith("/company/add");

  const isArchivedMode = location.pathname.startsWith("/company/archived");

  /**
   * An archived client reached through its own URL, which is a different thing from the legacy
   * archive route above and so needs its own check — the path looks exactly like a live client's.
   *
   * Kept separate rather than folded into isArchivedMode because that flag also decides basePath, and
   * an archived client's sections still live under /company/{guid}, not /company/archived/{id}.
   */
  const isArchivedClient = isClientArchived(companyOverview);

  /* ========================= */
  /* Base Path Builder */
  /* ========================= */

  const basePath = (() => {
    if (isAddMode) {
      return "/company/add";
    }

    if (isArchivedMode) {
      return `/company/archived/${companyRowID}`;
    }

    return `/company/${companyRowID}`;
  })();

  /* ========================= */
  /* Locked Tabs in Add Mode */
  /* ========================= */

  const lockedTabsInAddMode = [
    "overview",
    "sla",
    "actions",
    "sale-deals",
    "company-contacts",
    "company-offices",
    "comments",
    "activity",
    "imperans-link",
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        height: "calc(100vh - 155px)",
        borderRight: "1px solid #e6e6e6",
        paddingTop: 2,
      }}
    >
      {menu.map((group) => (
        <Box key={group.title} sx={{ mb: 1 }}>
          <Typography sx={{ paddingLeft: 2.5 }} className={classes.groupmenu}>
            {group.title}
          </Typography>

          <List sx={{ p: 0 }}>
            {group.items
              .filter((item) => {
                if (item.path === "revenue") {
                  return (
                    companyOverview &&
                    companyOverview.companyIntegrationDetail != null
                  );
                }
                return true;
              })
              .map((item) => {
              const Icon = iconMap[item.icon] || HelpOutline;

              /* ========================= */
              /* Determine Lock State */
              /* ========================= */

              const isLocked =
                ((isAddMode || isArchivedMode) &&
                  lockedTabsInAddMode.includes(item.path)) ||
                // Every section that writes is closed to an archived client; Overview, Details and
                // Activity stay open so the record is still readable.
                (isArchivedClient &&
                  ARCHIVED_CLIENT_LOCKED_SECTIONS.includes(item.path));

              /* ========================= */
              /* Build Navigation Path */
              /* ========================= */

              const itemPath = `${basePath}/${item.path}`;

              return (
                <ListItemButton
                  key={item.path}
                  component={NavLink}
                  to={itemPath}
                  // Section switches replace the history entry, so Back leaves the
                  // record to the originating grid rather than retracing panels (CR.9).
                  replace
                  disabled={isLocked}
                  onClick={(e) => {
                    if (isLocked) e.preventDefault();
                  }}
                  sx={{
                    px: 3,
                    py: 0.8,
                    color: "#333",

                    "& .menu-icon": {
                      color: "#4a4038",
                    },

                    "&.active": {
                      backgroundColor: "#fdf1e5",
                      color: "#ef7d00",
                      borderLeft: "3px solid #ef7d00",
                    },

                    "&.active .menu-icon": {
                      color: "#ef7d00",
                    },

                    ...(isLocked && {
                      opacity: 0.5,
                      pointerEvents: "none",
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                    }}
                  >
                    <Icon fontSize="small" className="menu-icon" />
                  </ListItemIcon>

                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 15,
                      fontWeight: 500,
                    }}
                  />

                  {/* Counted sections (Contacts, Offices) show their badge even at zero (CR.9). */}
                  {item.status !== MenuStatus.None || item.count ? (
                    <Badge
                      badgeContent={item.count}
                      showZero
                      color="warning"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: 11,
                          minWidth: 18,
                          height: 18,
                        },
                      }}
                    />
                  ) : null}

                  {item.status == MenuStatus.Incompleted && (
                    <div className={classes.incompleteBadge}></div>
                  )}

                  {item.status == MenuStatus.Completed && (
                    <div className={classes.completeBadge}></div>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};
export default observer(CompanySidebar);
